// gaji.repo — slip gaji (tabel payroll_slips, migrasi 0148). Akses Supabase saja; perhitungan
// ada di `@domain/gaji`, orkestrasi + guard izin di gaji.service.
import { supabase } from '@core/supabase';
import { pesanDbError } from '@core/dberr';
import { fetchAllPaged } from '@core/paging';
import { currentUser } from '@core/session';
import { genNo } from '@domain/format';
import type { Komponen } from '@domain/gaji';

export interface Slip {
  id: number; no: string | null; periode: string;
  emp_id: number; emp_nama: string; emp_role: string | null; outlet: string | null;
  gaji_pokok: number; tunjangan: Komponen[]; komisi: number; potongan: Komponen[]; netto: number;
  status: 'draft' | 'approved' | 'paid';
  catatan: string | null; expense_id: number | null;
  dibuat_by: string | null; dibuat_at: string | null;
  disetujui_by: string | null; disetujui_at: string | null;
  dibayar_by: string | null; dibayar_at: string | null; dibayar_via: string | null;
}

const KOL = 'id,no,periode,emp_id,emp_nama,emp_role,outlet,gaji_pokok,tunjangan,komisi,potongan,netto,status,catatan,expense_id,dibuat_by,dibuat_at,disetujui_by,disetujui_at,dibayar_by,dibayar_at,dibayar_via';

export async function listByPeriode(periode: string): Promise<Slip[]> {
  return await fetchAllPaged<Slip>((a, b) => supabase().from('payroll_slips')
    .select(KOL).eq('periode', periode).order('emp_nama').order('id').range(a, b));
}

/** Slip periode sebelumnya — dipakai mengisi ancang-ancang pokok & tunjangan bulan ini. */
export async function listSebelum(periode: string): Promise<Slip[]> {
  return await fetchAllPaged<Slip>((a, b) => supabase().from('payroll_slips')
    .select(KOL).lt('periode', periode).order('periode', { ascending: false }).order('id').range(a, b));
}

export interface SlipInput {
  periode: string; emp_id: number; emp_nama: string; emp_role: string | null; outlet: string | null;
  gaji_pokok: number; tunjangan: Komponen[]; komisi: number; potongan: Komponen[]; netto: number; catatan: string | null;
}

/**
 * Simpan (buat / perbarui) slip. Satu slip per orang per periode dijamin unique index — jadi
 * upsert, bukan insert: menekan Simpan dua kali tak boleh melahirkan gaji kedua.
 */
export async function simpanSlip(d: SlipInput): Promise<void> {
  const u = currentUser();
  const { data } = await supabase().from('payroll_slips')
    .select('id,no,status').eq('periode', d.periode).eq('emp_id', d.emp_id).maybeSingle();
  const ada = data as { id: number; no: string | null; status: string } | null;
  if (ada) {
    if (ada.status === 'paid') throw new Error('Slip sudah dibayar — angkanya tidak bisa diubah.');
    // Mengubah isi slip yang sudah disetujui MEMBATALKAN persetujuannya. Tanpa ini, persetujuan
    // cuma stempel: slip disetujui Rp 4 juta lalu diubah jadi Rp 40 juta dan langsung dibayar.
    const turunkan = ada.status === 'approved'
      ? { status: 'draft', disetujui_by: null, disetujui_at: null }
      : {};
    const { error } = await supabase().from('payroll_slips').update({ ...d, ...turunkan }).eq('id', ada.id);
    if (error) throw new Error(await pesanDbError(error.message));
    return;
  }
  const { error } = await supabase().from('payroll_slips').insert({
    ...d, no: genNo('GAJ'), status: 'draft', dibuat_by: u?.nama ?? null,
  });
  if (error) throw new Error(await pesanDbError(error.message));
}

export async function setujuiSlip(id: number): Promise<void> {
  const u = currentUser();
  const { error } = await supabase().from('payroll_slips')
    .update({ status: 'approved', disetujui_by: u?.nama ?? null, disetujui_at: new Date().toISOString() }).eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * KLAIM slip untuk dibayar — atomik. Mengembalikan false bila slip sudah tidak 'approved'
 * (sudah dibayar orang lain / layar basi). Dipanggil SEBELUM beban dicatat: dua tab yang
 * sama-sama menekan Bayar akan menghasilkan dua beban Gaji kalau klaimnya tidak di sini.
 */
export async function klaimBayar(id: number, via: string): Promise<boolean> {
  const u = currentUser();
  const { data, error } = await supabase().from('payroll_slips').update({
    status: 'paid', dibayar_by: u?.nama ?? null, dibayar_at: new Date().toISOString(), dibayar_via: via,
  }).eq('id', id).eq('status', 'approved').select('id');
  if (error) throw new Error(await pesanDbError(error.message));
  return (data ?? []).length > 0;
}

/** Batalkan klaim bila pencatatan bebannya gagal — slip kembali bisa dibayar, tanpa beban yatim. */
export async function batalKlaimBayar(id: number): Promise<void> {
  await supabase().from('payroll_slips')
    .update({ status: 'approved', dibayar_by: null, dibayar_at: null, dibayar_via: null }).eq('id', id);
}

/** Slip tunggal dari DB — dipakai sebelum membayar, supaya nettonya bukan angka layar yang basi. */
export async function getSlip(id: number): Promise<Slip | null> {
  const { data, error } = await supabase().from('payroll_slips').select(KOL).eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  return (data ?? null) as Slip | null;
}

export async function hapusSlip(id: number): Promise<void> {
  const { error } = await supabase().from('payroll_slips').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/** Karyawan aktif untuk menu Penggajian (RPC 0149 — RLS employees membatasi Finance ke dirinya). */
export interface EmpGaji { id: number; nama: string; role: string; outlet: string | null; aktif: boolean }
export async function listKaryawanGaji(): Promise<EmpGaji[]> {
  const { data, error } = await supabase().rpc('payroll_employees');
  if (error) throw new Error(error.message);
  return ((data ?? []) as EmpGaji[]).filter((e) => e.aktif).sort((a, b) => a.nama.localeCompare(b.nama));
}
